import {
  Button,
  Col,
  Form,
  Input,
  message,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from "antd";
import { CopyOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { RootState } from "../../app/store";
import { resetTrip, setCountry, setOrigin } from "../../features/map/mapSlice";
import { getAdmin } from "../../services/adminApi";
import {
  getGuestTripAccessByToken,
  GuestTripAccessCode,
  saveGuestTripAccessConfig,
  UpdateGuestTripAccessInput,
} from "../../services/guestTripAccessApi";
import { COUNTRIES } from "../../utils/constants/countries";
import ComponentPlacesAutocomplete from "../map/PlaceAutocomplete";
import ContainerLayout from "../common/containerLayout";
import "./styles.css";

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

const DEFAULT_BUTTON_TEXT = "Solicitar un Mowi";
const INTERNATIONAL_COUNTRY_ID = -1;

const getEnvLogoUrl = (): string =>
  (process.env.REACT_APP_MOWI_LOGO_URL || "").trim();

const buildGuestTripAccessPayload = (
  countryId: number | undefined,
  pickUp?: {
    originAddress?: string;
    originLatitude?: number;
    originLongitude?: number;
  } | null,
  buttonText?: string,
): UpdateGuestTripAccessInput => {
  const payload: UpdateGuestTripAccessInput = {
    buttonText: buttonText?.trim() || DEFAULT_BUTTON_TEXT,
  };

  if (countryId !== undefined && countryId !== null) {
    payload.countryId = countryId;
  }

  const address = pickUp?.originAddress?.trim();
  const latitude = pickUp?.originLatitude;
  const longitude = pickUp?.originLongitude;
  const hasValidCoords =
    latitude !== undefined &&
    latitude !== null &&
    longitude !== undefined &&
    longitude !== null &&
    Number.isFinite(Number(latitude)) &&
    Number.isFinite(Number(longitude));

  if (address && hasValidCoords) {
    payload.address = address;
    payload.latitude = Number(latitude);
    payload.longitude = Number(longitude);
  } else {
    payload.address = null;
    payload.latitude = null;
    payload.longitude = null;
  }

  return payload;
};

const buildGuestRequestTripUrl = (code: string): string => {
  const baseUrl = (process.env.REACT_APP_MOWI_MANAGER_GUEST_URL || "").replace(
    /\/$/,
    "",
  );
  if (!baseUrl) {
    return "";
  }
  return `${baseUrl}/guest/request-trip/${encodeURIComponent(code)}`;
};

const escapeHtmlAttribute = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const buildGuestTripEmbedHtml = (
  url: string,
  logoUrl: string,
  buttonText: string,
): string => {
  const safeUrl = escapeHtmlAttribute(url);
  const safeLogoUrl = logoUrl ? escapeHtmlAttribute(logoUrl) : "";
  const safeLabel = escapeHtmlAttribute(buttonText.trim() || DEFAULT_BUTTON_TEXT);

  const logoMarkup = safeLogoUrl
    ? `<img src="${safeLogoUrl}" alt="Logo de la empresa" referrerpolicy="no-referrer" style="height:24px;width:auto;min-width:24px;object-fit:contain;display:block;" />`
    : "";

  const dividerMarkup = safeLogoUrl
    ? `<span style="width:1px;height:22px;background-color:rgba(0,48,135,0.35);"></span>`
    : "";

  return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" style="display:inline-flex;align-items:center;gap:12px;padding:12px 22px;border:none;border-radius:8px;text-decoration:none;background-color:#ffc439;color:#003087;font-family:-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:16px;font-weight:600;line-height:1;box-shadow:0 1px 3px rgba(0,0,0,0.2);">
  ${logoMarkup}
  ${dividerMarkup}
  <span>${safeLabel}</span>
</a>`;
};

type GuestTripButtonPreviewProps = {
  url: string;
  logoUrl: string;
  buttonText: string;
  onLogoError: () => void;
};

const GuestTripButtonPreview = ({
  url,
  logoUrl,
  buttonText,
  onLogoError,
}: GuestTripButtonPreviewProps) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="pp-pay-button"
  >
    {logoUrl ? (
      <>
        <img
          className="pp-pay-button__company-logo"
          alt="Logo de la empresa"
          src={logoUrl}
          referrerPolicy="no-referrer"
          onError={onLogoError}
        />
        <span className="pp-pay-button__divider" aria-hidden="true" />
      </>
    ) : null}
    <span className="pp-pay-button__label">
      {buttonText.trim() || DEFAULT_BUTTON_TEXT}
    </span>
  </a>
);

const applyAccessConfigToForm = (
  config: GuestTripAccessCode,
  form: ReturnType<typeof Form.useForm>[0],
  dispatch: ReturnType<typeof useAppDispatch>,
) => {
  const country =
    COUNTRIES.find((item) => item.value === Number(config.countryId)) ??
    COUNTRIES[0];

  dispatch(setCountry(country));
  dispatch(resetTrip());

  const loadedAddress = config.address?.trim() || "";
  const latitude = Number(config.latitude);
  const longitude = Number(config.longitude);
  const hasValidCoords = Number.isFinite(latitude) && Number.isFinite(longitude);

  form.setFieldsValue({
    countryId: country.value,
    buttonText: config.buttonText?.trim() || DEFAULT_BUTTON_TEXT,
    address: loadedAddress || undefined,
  });

  if (loadedAddress && hasValidCoords) {
    dispatch(
      setOrigin({
        id: "persisted-origin",
        originAddress: loadedAddress,
        originLatitude: latitude,
        originLongitude: longitude,
      }),
    );
  }
};

const GuestTrips = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accessConfig, setAccessConfig] = useState<GuestTripAccessCode | null>(
    null,
  );
  const [savedOriginAddress, setSavedOriginAddress] = useState("");
  const [originAutocompleteKey, setOriginAutocompleteKey] = useState(0);
  const [adminLogoUrl, setAdminLogoUrl] = useState("");
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  const { destinations, country } = useAppSelector(
    (state: RootState) => state?.map,
  );

  const buttonText =
    Form.useWatch("buttonText", form) ||
    accessConfig?.buttonText?.trim() ||
    DEFAULT_BUTTON_TEXT;

  const companyLogoUrl = useMemo(() => {
    const envLogoUrl = getEnvLogoUrl();
    if (envLogoUrl) {
      return envLogoUrl;
    }
    return adminLogoUrl.trim();
  }, [adminLogoUrl]);

  const isInternational = country?.value === INTERNATIONAL_COUNTRY_ID;
  const placeCountryCode =
    !isInternational && country?.code && country.code !== "INT"
      ? country.code.toLowerCase()
      : undefined;

  const hasOrigin = Boolean(
    destinations?.pickUp?.originAddress?.trim() || savedOriginAddress.trim(),
  );

  const handleClearOrigin = () => {
    dispatch(resetTrip());
    setSavedOriginAddress("");
    form.setFieldValue("address", undefined);
    setOriginAutocompleteKey((current) => current + 1);
  };

  const generatedUrl = accessConfig?.code
    ? buildGuestRequestTripUrl(accessConfig.code)
    : "";

  const embedHtml = useMemo(() => {
    if (!generatedUrl) {
      return "";
    }

    return buildGuestTripEmbedHtml(
      generatedUrl,
      companyLogoUrl,
      buttonText,
    );
  }, [buttonText, companyLogoUrl, generatedUrl]);

  const initializeGuestTripButton = useCallback(async () => {
    const baseUrl = process.env.REACT_APP_MOWI_MANAGER_GUEST_URL || "";
    if (!baseUrl) {
      message.error(
        "Configura REACT_APP_MOWI_MANAGER_GUEST_URL para habilitar el acceso.",
      );
      return;
    }

    setPageLoading(true);
    try {
      let config = await getGuestTripAccessByToken();

      if (!config?.id || !config.code) {
        config = await saveGuestTripAccessConfig(null, {
          countryId: COUNTRIES[0].value,
          buttonText: DEFAULT_BUTTON_TEXT,
          address: null,
          latitude: null,
          longitude: null,
        });
      }

      setAccessConfig(config);
      const loadedAddress = config.address?.trim() || "";
      setSavedOriginAddress(loadedAddress);
      setLogoLoadFailed(false);
      applyAccessConfigToForm(config, form, dispatch);
      setOriginAutocompleteKey((current) => current + 1);
    } catch {
      message.error(
        "No se pudo cargar el boton de invitado. Verifique que los viajes de invitado esten habilitados.",
      );
    } finally {
      setPageLoading(false);
    }
  }, [dispatch, form]);

  useEffect(() => {
    void initializeGuestTripButton();
  }, [initializeGuestTripButton]);

  useEffect(() => {
    const loadAdminLogo = async () => {
      try {
        const admin = await getAdmin(0, true);
        if (admin?.picture?.trim()) {
          setAdminLogoUrl(admin.picture.trim());
        }
      } catch {
        // Keep env fallback only.
      }
    };

    loadAdminLogo();
  }, []);

  const handleSave = async () => {
    const baseUrl = process.env.REACT_APP_MOWI_MANAGER_GUEST_URL || "";
    if (!baseUrl) {
      message.error(
        "Configura REACT_APP_MOWI_MANAGER_GUEST_URL para habilitar el acceso.",
      );
      return;
    }

    const pickUp = destinations?.pickUp;
    const values = await form.validateFields();
    const payload = buildGuestTripAccessPayload(
      values.countryId ?? country?.value,
      pickUp,
      values.buttonText,
    );

    setSaving(true);
    try {
      const updated = await saveGuestTripAccessConfig(accessConfig, payload);

      setAccessConfig(updated);
      const loadedAddress = updated.address?.trim() || "";
      setSavedOriginAddress(loadedAddress);
      setLogoLoadFailed(false);
      applyAccessConfigToForm(updated, form, dispatch);
      setOriginAutocompleteKey((current) => current + 1);
      message.success(
        accessConfig?.id
          ? "Boton actualizado correctamente."
          : "Boton creado correctamente.",
      );
    } catch {
      message.error(
        accessConfig?.id
          ? "No se pudo actualizar el boton de invitado."
          : "No se pudo crear el boton de invitado.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCopyEmbed = async () => {
    if (!embedHtml) {
      return;
    }

    try {
      await navigator.clipboard.writeText(embedHtml);
      message.success("HTML copiado al portapapeles");
    } catch {
      message.error("No se pudo copiar el HTML al portapapeles");
    }
  };

  return (
    <ContainerLayout title="Viajes de invitado" showNotification={false}>
      <Row justify="center">
        <Col xs={24} md={14} lg={12}>
          <Spin spinning={pageLoading}>
            <div className="guest-trips">
              <Title level={4}>Acceso para pasajeros</Title>
              <Paragraph type="secondary">
                Tu boton se crea automaticamente con una URL fija. Puedes editar
                el texto, el pais y opcionalmente un origen. Para viajes
                internacionales el origen no es obligatorio. Guarda los cambios
                y copia el HTML para pegarlo en tu sitio web.
              </Paragraph>

              <Form
                form={form}
                layout="vertical"
                initialValues={{
                  countryId: COUNTRIES[0].value,
                  buttonText: DEFAULT_BUTTON_TEXT,
                }}
              >
                <Form.Item
                  name="buttonText"
                  label="Texto del boton"
                  rules={[
                    {
                      required: true,
                      message: "Ingrese el texto del boton",
                    },
                  ]}
                >
                  <Input placeholder="Solicitar un Mowi" maxLength={120} />
                </Form.Item>

                <Form.Item
                  name="countryId"
                  label="Pais"
                >
                  <Select
                    placeholder="Seleccione un pais"
                    options={COUNTRIES}
                    showSearch
                    optionFilterProp="label"
                    onChange={(value: number) => {
                      const nextCountry = COUNTRIES.find(
                        (item) => item.value === value,
                      );
                      if (nextCountry) {
                        dispatch(setCountry(nextCountry));
                        dispatch(resetTrip());
                        setSavedOriginAddress("");
                        form.setFieldValue("address", undefined);
                        setOriginAutocompleteKey((current) => current + 1);
                      }
                    }}
                  />
                </Form.Item>

                <Form.Item name="address" label="Origen (opcional)">
                  <Row gutter={8} align="middle" wrap={false}>
                    <Col flex="auto">
                      {!pageLoading && accessConfig ? (
                        <ComponentPlacesAutocomplete
                          key={`guest-origin-${accessConfig.id}-${originAutocompleteKey}`}
                          type="origin"
                          placeholder={
                            isInternational
                              ? "Buscar direccion de origen (opcional)"
                              : "Buscar direccion de origen"
                          }
                          worldwide={isInternational}
                          countryCode={placeCountryCode}
                          initialAddress={savedOriginAddress}
                        />
                      ) : (
                        <Input
                          disabled
                          placeholder="Cargando direccion de origen..."
                        />
                      )}
                    </Col>
                    <Col>
                      <Button
                        icon={<CloseCircleOutlined />}
                        onClick={handleClearOrigin}
                        disabled={!hasOrigin}
                      >
                        Limpiar
                      </Button>
                    </Col>
                  </Row>
                </Form.Item>
              </Form>

              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                <Button type="primary" loading={saving} onClick={handleSave}>
                  Guardar cambios
                </Button>

                {embedHtml ? (
                  <>
                    <div className="guest-trips__preview">
                      <Text type="secondary">Vista previa del boton</Text>
                      <div className="guest-trips__preview-content">
                        <GuestTripButtonPreview
                          url={generatedUrl}
                          logoUrl={logoLoadFailed ? "" : companyLogoUrl}
                          buttonText={buttonText}
                          onLogoError={() => setLogoLoadFailed(true)}
                        />
                      </div>
                      {logoLoadFailed ? (
                        <Text type="warning">
                          No se pudo cargar el logo desde{" "}
                          <Text code>{companyLogoUrl}</Text>. Verifica que la URL
                          en REACT_APP_MOWI_LOGO_URL sea publica y accesible.
                        </Text>
                      ) : null}
                      {companyLogoUrl ? (
                        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                          Logo usado:{" "}
                          <Text copyable={{ text: companyLogoUrl }}>
                            {companyLogoUrl}
                          </Text>
                        </Paragraph>
                      ) : null}
                    </div>

                    <div>
                      <Space
                        style={{
                          width: "100%",
                          justifyContent: "space-between",
                          marginBottom: 8,
                        }}
                      >
                        <Text type="secondary">Codigo HTML para embeber</Text>
                        <Button icon={<CopyOutlined />} onClick={handleCopyEmbed}>
                          Copiar HTML
                        </Button>
                      </Space>
                      <TextArea value={embedHtml} readOnly rows={10} />
                      <Paragraph
                        type="secondary"
                        style={{ marginTop: 8, marginBottom: 0 }}
                      >
                        URL del boton:{" "}
                        <Text copyable={{ text: generatedUrl }}>
                          {generatedUrl}
                        </Text>
                      </Paragraph>
                    </div>
                  </>
                ) : null}
              </Space>
            </div>
          </Spin>
        </Col>
      </Row>
    </ContainerLayout>
  );
};

export default GuestTrips;
